import { Tooltip } from "antd";
import React from "react";


const CDN_TEAM_LOGO = (id) =>
    id ? `https://media.api-sports.io/football/teams/${id}.png` : undefined;

const cx = (...a) => a.filter(Boolean).join(" ");

function zoneClassFromDescription(desc = "") {
    const d = String(desc).toLowerCase();
    if (!d) return "";
    if (d.includes("champions league") || d.includes("uefa champions")) return "px-rank--ucl";
    if (d.includes("europa league")) return "px-rank--uel";
    if (d.includes("conference")) return "px-rank--uecl";
    if (d.includes("relegation")) return "px-rank--releg";
    return "";
}

function rankClass(rank, desc) {
    const byDesc = zoneClassFromDescription(desc);
    if (byDesc) return byDesc;
    if (rank <= 1) return "px-rank--leader";
    if (rank <= 4) return "px-rank--top4";
    return "";
}

function pillClass(c) {
    const ch = (c || "").toUpperCase();
    if (ch === "W") return "px-pill--w";
    if (ch === "D") return "px-pill--d";
    if (ch === "L") return "px-pill--l";
    return "";
}


export default function MatchStandings({
    fixture,
    homeId,
    awayId,
    standings
}) {
    const highlightSet = React.useMemo(
        () => new Set([homeId, awayId].filter(Boolean).map(Number)),
        [homeId, awayId]
    );

    return (
        <section className="px-container">
            <div className="px-standings-wrap" aria-live="polite">
                <style>{standingsCSS}</style>

                <h5 className="section-title mb-0">
                    {fixture?.league?.name
                        ? `${fixture.league.name} — ${fixture.league.season} Standings`
                        : "Standings"}
                </h5>
                <p className="section-subtitle mb-3">
                    Live league table with P, W, D, L, Goals and PTS.
                    {fixture?.teams?.home?.name && fixture?.teams?.away?.name
                        ? ` ${fixture.teams.home.name} and ${fixture.teams.away.name} are highlighted for quick reference.`
                        : ""}
                </p>

                <div className="px-standings-card">
                    <div className="px-standings-heading">
                        <h3>Standings</h3>
                        <div className="px-legend">
                            <Tooltip title="Win"><span className="px-pill px-pill--w">W</span></Tooltip>
                            <Tooltip title="Draw"><span className="px-pill px-pill--d">D</span></Tooltip>
                            <Tooltip title="Lose"><span className="px-pill px-pill--l">L</span></Tooltip>
                        </div>
                    </div>

                    {standings.map(({ title, rows }, gi) => (
                        <div key={gi} className="px-table-wrap">
                            <div className="px-table-scroll">
                                <table className="px-table" role="table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 52, textAlign: "center" }}>#</th>
                                            <th style={{ minWidth: 220 }}>Team</th>
                                            <th>P</th>
                                            <th>W</th>
                                            <th>D</th>
                                            <th>L</th>
                                            <th>Goals</th>
                                            <th>PTS</th>
                                            <th style={{ minWidth: 120 }}>Form</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => {
                                            const isHi = highlightSet.has(Number(r.teamId));
                                            return (
                                                <tr key={`${gi}-${r.teamId}`} className={cx(isHi && "is-highlight")}>
                                                    <td style={{ textAlign: "center" }}>
                                                        <Tooltip title={r.description}>
                                                            <span className={cx("px-rank", rankClass(r.rank, r.description))}>
                                                                {r.rank}
                                                            </span>
                                                        </Tooltip>
                                                    </td>
                                                    <td>
                                                        <div className="px-team">
                                                            <img
                                                                src={CDN_TEAM_LOGO(r.teamId)}
                                                                alt=""
                                                                aria-hidden="true"
                                                                loading="lazy"
                                                                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                                                            />
                                                            <span className="px-team-name" title={r.teamName}>
                                                                {r.teamName}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>{r.played}</td>
                                                    <td>{r.win}</td>
                                                    <td>{r.draw}</td>
                                                    <td>{r.lose}</td>
                                                    <td><span className="px-goals">{r.gf}:{r.ga}</span></td>
                                                    <td><strong className="px-pts">{r.pts}</strong></td>
                                                    <td><FormBadges form={r.form} /></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FormBadges({ form = "" }) {
    const lastFive = String(form).trim().slice(0, 5).split("");
    if (!lastFive.length || lastFive[0] === "") return <span className="px-form-empty">—</span>;
    return (
        <div className="px-form">
            {lastFive.map((c, i) => (
                <span key={i} className={cx("px-pill", pillClass(c))}>{c}</span>
            ))}
        </div>
    );
}

const standingsCSS = `
.px-container { background:#f7f7f9; padding:30px; }
.px-standings-wrap { max-width: 1380px; margin: 0 auto; }
@media (max-width: 520px) { .px-container { padding:16px; } }

.px-standings-card {
  width: 100%;
  background: #fff;
  border: 1px solid rgba(2,6,23,.08);
  box-shadow: 0 8px 20px rgba(2,6,23,.06);
  overflow: hidden;
}
.px-standings-heading {
  display:flex; align-items:center; justify-content:space-between;
  padding: 12px 14px; border-bottom: 1px solid rgba(2,6,23,.06);
}
.px-standings-heading h3 { margin:0; font-size:16px; font-weight:800; color:#0f172a; }
.px-legend { display:flex; gap:6px; align-items:center; }

.px-group-title {
  padding: 10px 14px;
  font-weight: 800; color:#0f172a; background:#f8fafc;
  border-bottom: 1px solid rgba(2,6,23,.06);
}

.px-pill {
  display:inline-flex; align-items:center; justify-content:center;
  height:22px; min-width:22px; padding: 0 6px;
  border-radius:6px; font-size:12px; font-weight:800; color:#fff;
}
.px-pill--w { background:#22c55e; }
.px-pill--d { background:#94a3b8; }
.px-pill--l { background:#ef4444; }

.px-table-wrap + .px-table-wrap { border-top: 1px solid rgba(2,6,23,.06); }
.px-table-scroll { width:100%; overflow:auto; }
.px-table {
  width:100%; border-collapse:separate; border-spacing:0;
  font-size:14px; color:#0f172a;
}
.px-table thead th {
  position:sticky; top:0; z-index:1;
  background:#ffffff;
  color:#64748b; font-weight:700; text-align:left;
  padding:10px 12px; border-bottom:1px solid rgba(2,6,23,.08);
}
.px-table tbody td {
  padding:10px 12px; border-bottom:1px solid rgba(2,6,23,.06);
  vertical-align:middle;
}
.px-table tbody tr:hover { background:#f8fafc; }
.px-table tbody tr.is-highlight {
  background: rgba(37,99,235,.08);
  box-shadow: inset 0 0 0 1px rgba(37,99,235,.25);
}

.px-rank {
  display:inline-flex; align-items:center; justify-content:center;
  width:28px; height:28px; border-radius:999px;
  background:#f1f5f9; color:#334155; font-weight:800; font-size:12px;
}
.px-rank--leader { background:#dcfce7; color:#166534; }
.px-rank--top4 { background:#e0f2fe; color:#075985; }
.px-rank--ucl { background:#dbeafe; color:#1d4ed8; }
.px-rank--uel { background:#fde68a; color:#92400e; }
.px-rank--uecl { background:#bbf7d0; color:#065f46; }
.px-rank--releg { background:#fee2e2; color:#991b1b; }

.px-team { display:flex; align-items:center; gap:10px; }
.px-team img { width:22px; height:22px; object-fit:contain; image-rendering:-webkit-optimize-contrast; }
.px-team-name {
  font-weight:700; color:#0f172a; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:220px;
}

.px-goals { color:#334155; font-weight:700; }
.px-pts { color:#0f172a; font-weight:900; }

.px-form { display:flex; gap:6px; }
.px-form-empty { color:#94a3b8; }

.px-error { padding:12px 14px; color:#991b1b; background:#fee2e2; border-top:1px solid #fecaca; }

.px-skeleton { padding:8px 14px; }
.px-skeleton-row {
  height:36px; margin:6px 0; border-radius:8px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
  background-size: 400% 100%; animation: px-shimmer 1.2s infinite linear;
}
@keyframes px-shimmer { 0% {background-position: 100% 0} 100% {background-position: -100% 0} }

@media (max-width: 640px) {
  .px-table thead th:nth-child(4),
  .px-table thead th:nth-child(5),
  .px-table thead th:nth-child(6) { display:none; }
  .px-table tbody td:nth-child(4),
  .px-table tbody td:nth-child(5),
  .px-table tbody td:nth-child(6) { display:none; }
  .px-team-name { max-width:140px; }
}
`;
